import React, { useState } from 'react';
import { Lock, Shield, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { sendFeatureRequestAccessNotification } from 'utils/firebase';
import { useAlert } from 'components/UI_components/Alert';

const RequireAccess = ({ currentMember, permission, module }) => {
  const { addAlert } = useAlert();
  const [requested, setRequested] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if user has access
  if (currentMember?.permissions?.[permission] || currentMember?.joined_as === 'owner' || currentMember?.is_admin) {
    return null; // User has access, don't render anything
  }

  const handleRequestAccess = async () => {
    if (requested || loading) return;

    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const org = JSON.parse(localStorage.getItem('organization') || '{}');
      const org_id = org?.id;

      const res = await sendFeatureRequestAccessNotification(
        userData,
        module,
        null, // No site_id for org level request
        null, // No site_data
        org_id
      );

      if (res?.throttled) {
        addAlert("A request was already sent within the last hour. Please wait.", "warning");
        setRequested(true);
      } else if (res?.success) {
        addAlert("Access request sent successfully to administrators!", "success");
        setRequested(true);
      }
    } catch (error) {
      addAlert("Failed to send access request. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Map module names to icons and descriptions
  const moduleInfo = {
    Finance: {
      icon: '💰',
      description: 'financial data and reporting'
    },
    Vendor: {
      icon: '🏢',
      description: 'vendor management and relationships'
    },
    Contractor: {
      icon: '👷',
      description: 'contractor information and assignments'
    },
    Procurement: {
      icon: '📦',
      description: 'procurement processes and orders'
    },
    'Organization Team': {
      icon: '👥',
      description: 'team management and organizational structure'
    },
    Projects: {
      icon: '📊',
      description: 'project planning and execution'
    }
  };

  const currentModule = moduleInfo[module] || {
    icon: '🔒',
    description: 'this module'
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col  w-[84%] h-[90vh] items-center justify-center">
      <div className="w-[45vw] py-2">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-3 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full mb-2">
              <Lock className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Access Required</h1>
            <p className="text-slate-300 text-sm">
              You don't have permission to view this content
            </p>
          </div>

          {/* Content Section */}
          <div className="px-8 py-4">
            {/* Module Info */}
            <div className="flex items-start gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
              <div className="text-3xl">{currentModule.icon}</div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">{module}</h3>
                <p className="text-sm text-slate-600">
                  Access to {currentModule.description}
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold text-blue-600">1</span>
                </div>
                <div>
                  <p className="text-sm text-slate-700">
                    Contact your <span className="font-semibold text-slate-900">organization administrator</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold text-blue-600">2</span>
                </div>
                <div>
                  <p className="text-sm text-slate-700">
                    Request access to the <span className="font-semibold text-slate-900">{module}</span> module
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold text-blue-600">3</span>
                </div>
                <div>
                  <p className="text-sm text-slate-700">
                    Refresh this page once access has been granted
                  </p>
                </div>
              </div>
            </div>

            {/* Request Button */}
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleRequestAccess}
                disabled={requested || loading}
                className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg ${requested
                  ? "bg-emerald-600 text-white cursor-default shadow-emerald-200"
                  : "bg-slate-900 hover:bg-slate-800 text-white disabled:bg-slate-300 disabled:shadow-none"
                  }`}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : requested ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <Shield className="w-5 h-5" />
                )}
                <span className="text-base font-bold">
                  {loading ? "Sending..." : requested ? "Access Requested!" : "Request One-Click Access"}
                </span>
              </button>
            </div>

            {/* Info Box */}
            <div className="mt-6 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                Access permissions are managed by your organization administrators to ensure proper data security and compliance.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-6 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <Shield className="w-4 h-4" />
              <span className="text-xs font-medium">Protected by SiteMaster Security</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequireAccess;